import { type Layout, type Pos, layoutById } from './layouts.js';
import { type Face, facesMatch, matchKey, seededRandom, shuffle, shuffledPairs } from './tiles.js';

export interface MjTile extends Pos {
  /** Index into the layout's positions; stable for the whole game. */
  readonly id: number;
  readonly face: Face;
}

export interface MjGame {
  readonly layout: string;
  readonly seed: number;
  readonly tiles: readonly MjTile[];
  /** Tile ids in the order they were removed (two per move). */
  readonly removed: readonly number[];
  /** `undo` may not go below this many removed tiles (a reshuffle redeals the faces, so older moves cannot be taken back). */
  readonly undoFloor: number;
  readonly shuffles: number;
}

export class IllegalMahjongMove extends Error {}

/** Is tile `i` free: nothing lies on top of it, and its left or its right side is open. Only tiles in `alive` count as present. */
function isFreeIn(tiles: readonly Pos[], alive: ReadonlySet<number>, i: number): boolean {
  const t = tiles[i]!;
  let left = false;
  let right = false;
  for (const j of alive) {
    if (j === i) continue;
    const o = tiles[j]!;
    if (Math.abs(o.y - t.y) >= 2) {
      continue;
    }
    if (o.layer === t.layer + 1 && Math.abs(o.x - t.x) < 2) return false;
    if (o.layer === t.layer) {
      if (o.x < t.x && t.x - o.x <= 2) left = true;
      else if (o.x > t.x && o.x - t.x <= 2) right = true;
    }
  }
  return !(left && right);
}

const freeIn = (tiles: readonly Pos[], alive: ReadonlySet<number>): number[] => [...alive].filter((i) => isFreeIn(tiles, alive, i));

/**
 * A removal order over `ids`: repeatedly take two currently-free tiles. Null when it gets stuck (fewer than two free tiles while some remain),
 * in which case the caller tries again with the next random draw.
 */
function removalOrder(tiles: readonly Pos[], ids: readonly number[], random: () => number): [number, number][] | null {
  const alive = new Set(ids);
  const order: [number, number][] = [];
  while (alive.size > 0) {
    const free = freeIn(tiles, alive);
    if (free.length < 2) return null;
    const pick = shuffle(free, random);
    const a = pick[0]!;
    const b = pick[1]!;
    alive.delete(a);
    alive.delete(b);
    order.push([a, b]);
  }
  return order;
}

export interface Deal {
  readonly game: MjGame;
  /** The pairs in a winning removal order. A test replays it; a screen never needs it. */
  readonly solution: readonly (readonly [number, number])[];
}

/**
 * Deal a board that can always be cleared: choose a removal order on the empty layout first, then give the i-th pair of tiles the i-th
 * matching pair of faces. A plain shuffle leaves many boards unwinnable.
 */
export function dealMahjongSolitaire(layoutId: string, seed: number): Deal {
  const layout: Layout = layoutById(layoutId);
  const n = layout.positions.length;
  if (n % 2 !== 0) throw new Error(`layout ${layout.id} has an odd tile count`);
  const random = seededRandom(seed);
  const ids = layout.positions.map((_, i) => i);
  let order: [number, number][] | null = null;
  for (let attempt = 0; attempt < 200 && !order; attempt++) order = removalOrder(layout.positions, ids, random);
  if (!order) throw new Error(`could not deal layout ${layout.id} for seed ${seed}`);
  const pairs = shuffledPairs(random).slice(0, n / 2);
  const faces = new Array<Face>(n);
  order.forEach(([a, b], k) => {
    const [fa, fb] = pairs[k]!;
    faces[a] = fa;
    faces[b] = fb;
  });
  const tiles = layout.positions.map((p, id) => ({ ...p, id, face: faces[id]! }));
  return { game: { layout: layout.id, seed, tiles, removed: [], undoFloor: 0, shuffles: 0 }, solution: order };
}

export const newMahjongSolitaire = (layoutId: string, seed: number): MjGame => dealMahjongSolitaire(layoutId, seed).game;

const aliveSet = (game: MjGame): Set<number> => {
  const gone = new Set(game.removed);
  return new Set(game.tiles.filter((t) => !gone.has(t.id)).map((t) => t.id));
};

export const tilesLeft = (game: MjGame): number => game.tiles.length - game.removed.length;
export const isWon = (game: MjGame): boolean => tilesLeft(game) === 0;
export const movesMade = (game: MjGame): number => game.removed.length / 2;

/** The ids of the tiles that can be picked right now. */
export function freeTiles(game: MjGame): number[] {
  return freeIn(game.tiles, aliveSet(game));
}

export function canMatch(game: MjGame, a: number, b: number): boolean {
  if (a === b) return false;
  const free = new Set(freeTiles(game));
  if (!free.has(a) || !free.has(b)) return false;
  return facesMatch(game.tiles[a]!.face, game.tiles[b]!.face);
}

/** Every pair of free tiles that match, each pair once, lowest ids first. */
export function availablePairs(game: MjGame): [number, number][] {
  const free = freeTiles(game).sort((x, y) => x - y);
  const out: [number, number][] = [];
  for (let i = 0; i < free.length; i++) {
    for (let j = i + 1; j < free.length; j++) {
      if (facesMatch(game.tiles[free[i]!]!.face, game.tiles[free[j]!]!.face)) out.push([free[i]!, free[j]!]);
    }
  }
  return out;
}

/** The first matching free pair, or null when stuck. */
export const hint = (game: MjGame): [number, number] | null => availablePairs(game)[0] ?? null;

export const isStuck = (game: MjGame): boolean => !isWon(game) && availablePairs(game).length === 0;

export function removePair(game: MjGame, a: number, b: number): MjGame {
  if (!canMatch(game, a, b)) throw new IllegalMahjongMove(`tiles ${a} and ${b} are not a free matching pair`);
  return { ...game, removed: [...game.removed, a, b] };
}

export function canUndo(game: MjGame): boolean {
  return game.removed.length > game.undoFloor;
}

/** Take back the last move, as many times as wanted (back to the last shuffle). */
export function undo(game: MjGame): MjGame {
  return canUndo(game) ? { ...game, removed: game.removed.slice(0, -2) } : game;
}

/**
 * Redeal the faces of the tiles still on the board so the rest can be cleared again, for a player who is stuck. The positions stay; the
 * faces are re-paired along a fresh removal order. History before the shuffle cannot be undone.
 */
export function reshuffle(game: MjGame): MjGame {
  const alive = [...aliveSet(game)].sort((x, y) => x - y);
  if (alive.length === 0) return game;
  const shuffles = game.shuffles + 1;
  const random = seededRandom((game.seed ^ Math.imul(shuffles, 0x9e3779b1)) >>> 0);
  // Pair the remaining faces up by their match key (flower with flower, season with season).
  const byKey = new Map<string, Face[]>();
  for (const id of alive) {
    const f = game.tiles[id]!.face;
    const k = matchKey(f);
    byKey.set(k, [...(byKey.get(k) ?? []), f]);
  }
  const pairs: [Face, Face][] = [];
  for (const faces of byKey.values()) {
    const mixed = shuffle(faces, random);
    for (let i = 0; i + 1 < mixed.length; i += 2) pairs.push([mixed[i]!, mixed[i + 1]!]);
  }
  let order: [number, number][] | null = null;
  for (let attempt = 0; attempt < 200 && !order; attempt++) order = removalOrder(game.tiles, alive, random);
  if (!order) return game;
  const next = shuffle(pairs, random);
  const faceOf = new Map<number, Face>();
  order.forEach(([a, b], k) => {
    faceOf.set(a, next[k]![0]);
    faceOf.set(b, next[k]![1]);
  });
  const tiles = game.tiles.map((t) => (faceOf.has(t.id) ? { ...t, face: faceOf.get(t.id)! } : t));
  return { ...game, tiles, shuffles, undoFloor: game.removed.length };
}
